import { emit, on, onPageScroll, pageScrollTop, zonedParts, parseTime, prefersReducedMotion } from '@lucid/core';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { StandardEvents } from '@shopify/events';
import { addToCart } from '@lucid/card';
import { getScrollTop, scrollTo as scrollPageTo } from '@theme/scroll-container';

const TZ = 'Europe/Berlin';
const DAY = 86400000;

/* Variant sync: re-renders every [data-lucid-sync] element from the page HTML the variant picker already fetched. */

let syncBound = false;

function bindVariantSync() {
  if (syncBound) return;
  syncBound = true;

  document.addEventListener(StandardEvents.productSelect, (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source || source.closest('product-card, quick-add-component')) return;
    const scope = source.closest('dialog, .shopify-section') || document;

    event.promise
      ?.then(({ detail, variant }) => {
        const html = detail?.html;
        if (!html) return;

        scope.querySelectorAll('[data-lucid-sync]').forEach((element) => {
          if (element.closest('product-card')) return;
          const key = element.getAttribute('data-lucid-sync');
          const next = html.querySelector(`[data-lucid-sync="${CSS.escape(key)}"]`);
          if (next) morph(element, next);
        });

        const resource = detail?.resource ?? null;
        emit('product:variant', {
          variantId: resource?.id ? String(resource.id) : null,
          available: Boolean(resource?.available),
          resource,
          variant,
          scope,
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn(error);
      });
  });
}

bindVariantSync();

/* Policy tables in the accordions (shipping costs): each cell gets its column head as label, CSS stacks rows on phones. */

function stackTables(root = document) {
  root.querySelectorAll('.lucid-accordion__source table:not(.is-stacked)').forEach((table) => {
    const head = table.tHead?.rows[0];
    if (!head || !table.tBodies.length) return;
    const labels = Array.from(head.cells).map((cell) => cell.textContent?.trim() || '');
    Array.from(table.tBodies).forEach((body) => {
      Array.from(body.rows).forEach((row) => {
        Array.from(row.cells).forEach((cell, index) => {
          if (labels[index]) cell.dataset.label = labels[index];
        });
      });
    });
    table.classList.add('is-stacked');
  });
}

stackTables();

/* Gallery: crossfade slider on the product ground, swipe and arrow keys, lightbox with thumbnails and zoom. */

const SWIPE = 50;

class LucidGallery extends HTMLElement {
  #index = 0;
  #slides = [];
  #drag = null;
  #lbIndex = 0;
  #lbSwipe = null;
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#slides = Array.from(this.querySelectorAll('.lucid-gallery__slide'));
    const panel = this.querySelector('[data-lucid-panel]');
    const stage = this.querySelector('[data-lucid-stage]');

    this.querySelector('[data-lucid-prev]')?.addEventListener('click', () => this.show(this.#index - 1, -1), { signal });
    this.querySelector('[data-lucid-next]')?.addEventListener('click', () => this.show(this.#index + 1, 1), { signal });
    this.querySelector('[data-lucid-expand]')?.addEventListener('click', () => this.openLightbox(this.#index), { signal });
    this.querySelector('[data-lucid-thumbs]')?.addEventListener(
      'click',
      (event) => {
        const thumb = event.target instanceof Element ? event.target.closest('[data-index]') : null;
        if (!thumb) return;
        const index = Number(thumb.getAttribute('data-index'));
        this.show(index, index > this.#index ? 1 : -1);
      },
      { signal }
    );

    panel?.addEventListener(
      'keydown',
      (event) => {
        if (event.target !== panel) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.#index + 1, 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.#index - 1, -1);
        } else if (event.key === 'Enter') {
          this.openLightbox(this.#index);
        }
      },
      { signal }
    );

    stage?.addEventListener('pointerdown', this.#onPointerDown, { signal });
    stage?.addEventListener('pointermove', this.#onPointerMove, { signal });
    stage?.addEventListener('pointerup', this.#onPointerUp, { signal });
    stage?.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    stage?.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    this.#bindLightbox(signal);

    const scope = this.closest('.shopify-section, dialog');
    scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#unlock();
  }

  get count() {
    return this.#slides.length;
  }

  show(target, direction = 0) {
    const count = this.count;
    if (count < 2) return;
    const next = (target + count) % count;
    if (next === this.#index) return;
    const incoming = this.#slides[next];
    const outgoing = this.#slides[this.#index];
    const animate = !prefersReducedMotion() && direction !== 0;

    if (animate) {
      incoming.classList.add('is-dragging', direction > 0 ? 'from-end' : 'from-start');
      void incoming.offsetWidth;
      incoming.classList.remove('is-dragging');
      outgoing.style.setProperty('--lc-gal-dx', `${direction > 0 ? -48 : 48}px`);
      window.setTimeout(() => outgoing.style.removeProperty('--lc-gal-dx'), 700);
    }

    outgoing.classList.remove('is-active');
    outgoing.setAttribute('aria-hidden', 'true');
    incoming.classList.add('is-active');
    incoming.removeAttribute('aria-hidden');
    requestAnimationFrame(() => incoming.classList.remove('from-end', 'from-start'));
    this.#index = next;
    this.#meta();
  }

  #meta() {
    const current = this.querySelector('[data-lucid-current]');
    if (current) current.textContent = String(this.#index + 1);
    const caption = this.querySelector('[data-lucid-caption]');
    if (caption) caption.textContent = this.#slides[this.#index]?.dataset.caption || '';
    const row = this.querySelector('[data-lucid-thumbs]');
    if (!(row instanceof HTMLElement)) return;
    row.querySelectorAll('[data-index]').forEach((thumb) => {
      if (Number(thumb.getAttribute('data-index')) === this.#index) {
        thumb.setAttribute('aria-current', 'true');
        if (row.scrollWidth > row.clientWidth && thumb instanceof HTMLElement) {
          const left = thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2;
          row.scrollTo({ left: Math.max(0, left), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  #onPointerDown = (event) => {
    if (event.button !== 0) return;
    this.#drag = { x: event.clientX, y: event.clientY, id: event.pointerId, moved: false, slide: this.#slides[this.#index] };
  };

  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId || this.count < 2) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
      drag.moved = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.currentTarget.classList.add('is-dragging');
      drag.slide.classList.add('is-dragging');
    }
    if (drag.moved) drag.slide.style.setProperty('--lc-gal-dx', `${dx * 0.6}px`);
  };

  #onPointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;
    this.#drag = null;
    const dx = event.clientX - drag.x;
    event.currentTarget.classList.remove('is-dragging');
    drag.slide.classList.remove('is-dragging');
    drag.slide.style.removeProperty('--lc-gal-dx');
    if (drag.moved && Math.abs(dx) > SWIPE) {
      this.show(this.#index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
    } else if (!drag.moved) {
      this.openLightbox(this.#index);
    }
  };

  #onPointerCancel = (event) => {
    const drag = this.#drag;
    this.#drag = null;
    event.currentTarget.classList.remove('is-dragging');
    if (!drag) return;
    drag.slide.classList.remove('is-dragging');
    drag.slide.style.removeProperty('--lc-gal-dx');
  };

  #onProductSelect = (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source || source.closest('product-card')) return;
    const grid = this.closest('[data-product-grid-content]');
    if (grid && !grid.contains(source)) return;
    event.promise
      ?.then(({ detail }) => {
        const next = detail?.html?.querySelector('lucid-gallery');
        if (!next || next.getAttribute('data-signature') === this.getAttribute('data-signature')) return;
        this.replaceWith(document.importNode(next, true));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn(error);
      });
  };

  /* Lightbox */

  get #lightbox() {
    return this.querySelector('[data-lucid-lightbox]');
  }

  get #imageSlides() {
    return this.#slides.filter((slide) => slide.dataset.full);
  }

  #bindLightbox(signal) {
    const dialog = this.#lightbox;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const stage = dialog.querySelector('[data-lucid-lb-stage]');

    dialog.querySelector('[data-lucid-lb-close]')?.addEventListener('click', () => dialog.close(), { signal });
    dialog.querySelector('[data-lucid-lb-prev]')?.addEventListener('click', () => this.#lbShow(this.#lbIndex - 1, -1), { signal });
    dialog.querySelector('[data-lucid-lb-next]')?.addEventListener('click', () => this.#lbShow(this.#lbIndex + 1, 1), { signal });
    dialog.querySelector('[data-lucid-lb-thumbs]')?.addEventListener(
      'click',
      (event) => {
        const thumb = event.target instanceof Element ? event.target.closest('[data-index]') : null;
        if (!thumb) return;
        const position = this.#imageSlides.indexOf(this.#slides[Number(thumb.getAttribute('data-index'))]);
        if (position > -1) this.#lbShow(position, position > this.#lbIndex ? 1 : -1);
      },
      { signal }
    );
    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.#lbShow(this.#lbIndex + 1, 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.#lbShow(this.#lbIndex - 1, -1);
        }
      },
      { signal }
    );
    dialog.addEventListener('close', this.#onLightboxClose, { signal });

    stage?.addEventListener(
      'pointerdown',
      (event) => {
        this.#lbSwipe = { x: event.clientX, y: event.clientY, swiped: false };
      },
      { signal }
    );
    stage?.addEventListener(
      'pointerup',
      (event) => {
        const start = this.#lbSwipe;
        if (!start || stage.classList.contains('is-zoomed')) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) {
          start.swiped = true;
          this.#lbShow(this.#lbIndex + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
        }
      },
      { signal }
    );
    stage?.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || event.target.closest('button')) return;
        if (this.#lbSwipe?.swiped) return;
        const image = stage.querySelector('[data-lucid-lb-img]');
        if (!(image instanceof HTMLElement)) return;
        const rect = stage.getBoundingClientRect();
        image.style.transformOrigin = `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)}% ${(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)}%`;
        stage.classList.toggle('is-zoomed');
      },
      { signal }
    );
  }

  openLightbox(index) {
    const dialog = this.#lightbox;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    const images = this.#imageSlides;
    if (!images.length) return;
    const position = Math.max(0, images.indexOf(this.#slides[index]));
    this.#lbShow(position, 0);
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.showModal();
    dialog.querySelector('[data-lucid-lb-close]')?.focus({ preventScroll: true });
  }

  #lbShow(target, direction) {
    const dialog = this.#lightbox;
    const images = this.#imageSlides;
    if (!dialog || !images.length) return;
    const position = (target + images.length) % images.length;
    this.#lbIndex = position;
    const slide = images[position];
    const image = dialog.querySelector('[data-lucid-lb-img]');
    dialog.querySelector('[data-lucid-lb-stage]')?.classList.remove('is-zoomed');

    if (image instanceof HTMLImageElement) {
      const apply = () => {
        image.src = slide.dataset.full || '';
        image.alt = slide.dataset.alt || '';
        image.classList.remove('is-out');
      };
      if (direction && !prefersReducedMotion()) {
        image.classList.add('is-out');
        window.setTimeout(apply, 180);
      } else {
        apply();
      }
    }

    const count = dialog.querySelector('[data-lucid-lb-count]');
    if (count) count.textContent = `${this.#slides.indexOf(slide) + 1} / ${this.count}`;
    dialog.querySelectorAll('.lucid-lightbox__thumb').forEach((thumb) => {
      const active = Number(thumb.getAttribute('data-index')) === this.#slides.indexOf(slide);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  #onLightboxClose = () => {
    this.#unlock();
    const slide = this.#imageSlides[this.#lbIndex];
    const index = this.#slides.indexOf(slide);
    if (index > -1 && index !== this.#index) this.show(index, index > this.#index ? 1 : -1);
  };

  #unlock() {
    if (!document.querySelector('dialog.lucid-lightbox[open], dialog.lucid-buybar__sheet[open]')) document.documentElement.removeAttribute('scroll-lock');
  }
}

if (!customElements.get('lucid-gallery')) customElements.define('lucid-gallery', LucidGallery);

/* Buy box: best-selling size preselected (else the shopper picks), size nudge, add to cart states, notify, wash
   prefetch. One per product section. */

const prefetched = new Set();

function prefetch(url) {
  if (!url || prefetched.has(url)) return;
  prefetched.add(url);
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = 'document';
  link.href = url;
  document.head.append(link);
}

class LucidPdpCore extends HTMLElement {
  #controller = null;
  #doneTimer = 0;
  /** @type {Map<string, string>} option value id the shopper chose, per size fieldset index */
  #chosen = new Map();
  /** @type {HTMLElement | null} buy button pressed while the preselected size was still loading */
  #queued = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.section;

    this.#readChosen();
    this.#apply();

    window.addEventListener('click', this.#onClickCapture, { capture: true, signal });
    window.addEventListener('submit', this.#onSubmitCapture, { capture: true, signal });
    section.addEventListener('change', this.#onChange, { signal });
    section.addEventListener('click', this.#onClick, { signal });
    section.addEventListener('pointerover', this.#onHover, { signal });
    section.addEventListener('focusin', this.#onHover, { signal });
    section.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearTimeout(this.#doneTimer);
  }

  get section() {
    return this.closest('.shopify-section') || document.body;
  }

  get picker() {
    return this.section.querySelector('variant-picker[data-template-product-match="true"]');
  }

  get form() {
    return this.section.querySelector('.product-details product-form-component');
  }

  get unpicked() {
    return this.section.dataset.lucidSize === 'none';
  }

  /** The preselected size differs from the rendered variant and its data is still loading. */
  get loading() {
    return Boolean(this.picker?.hasAttribute('data-lucid-default-loading'));
  }

  /** Size fieldsets that must be chosen by the shopper (no preselection). */
  #pickFieldsets() {
    const picker = this.picker;
    if (!picker || !picker.hasAttribute('data-lucid-no-preselect') || this.dataset.productAvailable !== 'true') return [];
    return Array.from(picker.querySelectorAll('fieldset[data-lucid-pick]')).filter(
      (fieldset) => fieldset.querySelectorAll('input[type="radio"]').length > 1
    );
  }

  #readChosen() {
    this.#chosen.clear();
    for (const fieldset of this.#pickFieldsets()) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement && checked.dataset.optionValueId) {
        this.#chosen.set(fieldset.dataset.fieldsetIndex || '', checked.dataset.optionValueId);
      }
    }
  }

  /** Mirrors the chosen sizes onto the (possibly re-rendered) picker and sets the section state. */
  #apply() {
    const fieldsets = this.#pickFieldsets();
    let missing = false;
    for (const fieldset of fieldsets) {
      const chosen = this.#chosen.get(fieldset.dataset.fieldsetIndex || '');
      let label = '';
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const on = Boolean(chosen) && input.dataset.optionValueId === chosen;
        if (input.checked !== on) input.checked = on;
        input.dataset.currentChecked = String(on);
        if (on) label = input.value;
      });
      const selected = fieldset.querySelector('.lucid-sizes__selected');
      if (selected && selected.textContent !== label) selected.textContent = label;
      if (!chosen) missing = true;
    }
    const section = this.section;
    if (missing) {
      section.dataset.lucidSize = 'none';
    } else {
      delete section.dataset.lucidSize;
      section.classList.remove('lucid-is-asking');
    }
    this.picker?.toggleAttribute('data-lucid-unpicked', missing);
    this.#markFit(missing);
    emit('size:state', { unpicked: missing, scope: section });
  }

  /** Highlights the chosen size column in the fit box. */
  #markFit(missing) {
    const checked = missing ? null : this.picker?.querySelector('fieldset input[type="radio"]:checked');
    const size = checked instanceof HTMLInputElement ? checked.value.replace(/\s+/g, '') : '';
    this.section.querySelectorAll('[data-lucid-fitbox] [data-size]').forEach((cell) => {
      cell.classList.toggle('is-active', Boolean(size) && cell.getAttribute('data-size') === size);
    });
  }

  /** Records the size the shopper chose (input of the main picker) and updates the state. */
  pick(input) {
    if (input instanceof HTMLInputElement) {
      const fieldset = input.closest('fieldset[data-lucid-pick]');
      if (fieldset && input.dataset.optionValueId) this.#chosen.set(fieldset.dataset.fieldsetIndex || '', input.dataset.optionValueId);
    }
    this.#apply();
  }

  nudge() {
    const picker = this.picker;
    if (!picker) return;
    this.section.classList.add('lucid-is-asking');
    const fieldsets = this.#pickFieldsets();
    const open = fieldsets.find((fieldset) => !this.#chosen.has(fieldset.dataset.fieldsetIndex || ''));
    const grid = open || picker.querySelector('.variant-option--buttons') || picker;
    grid.classList.remove('lucid-nudge');
    void grid.offsetWidth;
    grid.classList.add('lucid-nudge');
    const rect = grid.getBoundingClientRect();
    if (rect.top < 90 || rect.bottom > window.innerHeight - 90) {
      scrollPageTo({ top: getScrollTop() + rect.top - window.innerHeight / 3, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    window.setTimeout(() => {
      const target = grid.querySelector('input[type="radio"][data-option-available="true"]') || grid.querySelector('input[type="radio"]');
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }, 400);
  }

  notify() {
    const input = this.form?.querySelector('input[name="id"]');
    const sizePicked = !this.picker || Boolean(this.picker.querySelector('input[type="radio"]:checked'));
    const variantId = input instanceof HTMLInputElement && sizePicked ? input.value : '';
    emit('bis:open', { variantId });
  }

  #isMainSubmit(target) {
    const button = target instanceof Element ? target.closest('[ref="addToCartButton"]') : null;
    const form = this.form;
    return button && form && form.contains(button) ? button : null;
  }

  #onClickCapture = (event) => {
    const button = this.#isMainSubmit(event.target);
    if (button && this.loading) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#queued = button;
      return;
    }
    if (!this.unpicked) return;
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.nudge();
  };

  #onSubmitCapture = (event) => {
    const form = this.form;
    if (!form || !(event.target instanceof HTMLFormElement) || !form.contains(event.target)) return;
    if (this.loading) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#queued = form.querySelector('[ref="addToCartButton"]');
      return;
    }
    if (this.unpicked) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.nudge();
      return;
    }
    const button = form.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLElement && !button.hasAttribute('disabled')) {
      window.clearTimeout(this.#doneTimer);
      button.dataset.lucidState = 'loading';
    }
  };

  #onChange = (event) => {
    const picker = this.picker;
    if (picker && event.target instanceof HTMLInputElement && picker.contains(event.target)) this.pick(event.target);
  };

  #onProductSelect = (event) => {
    const picker = this.picker;
    if (!picker || !(event.target instanceof Node) || !picker.contains(event.target)) return;
    event.promise
      ?.then(() => {
        this.picker?.removeAttribute('data-lucid-default-loading');
        this.#apply();
        const queued = this.#queued;
        this.#queued = null;
        if (queued) this.form?.querySelector('[ref="addToCartButton"]')?.click();
      })
      .catch(() => {
        this.picker?.removeAttribute('data-lucid-default-loading');
        this.#queued = null;
      });
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const picker = this.picker;
    if (picker && picker.contains(target) && target.closest('label')) {
      const input = target.closest('label')?.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement && input.checked) this.pick(input);
    }
    if (target.closest('[data-lucid-notify]')) {
      event.preventDefault();
      this.notify();
    }
  };

  #onHover = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-lucid-prefetch]') : null;
    if (link instanceof HTMLAnchorElement && this.section.contains(link)) prefetch(link.href);
  };

  #onCartUpdate = (event) => {
    const form = this.form;
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    const button = form.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLElement)) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) {
          delete button.dataset.lucidState;
          return;
        }
        button.dataset.lucidState = 'done';
        this.#doneTimer = window.setTimeout(() => delete button.dataset.lucidState, 1800);
      })
      .catch(() => delete button.dataset.lucidState);
  };
}

if (!customElements.get('lucid-pdp-core')) customElements.define('lucid-pdp-core', LucidPdpCore);

/* Delivery estimate: cut-off time, processing days and shipping days in business days, weekends skipped. */

function addBusinessDays(utcDate, days) {
  let date = utcDate;
  let remaining = days;
  while (remaining > 0) {
    date = new Date(date.getTime() + DAY);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6) remaining -= 1;
  }
  return date;
}

function nextBusinessDay(utcDate) {
  let date = utcDate;
  while (date.getUTCDay() === 0 || date.getUTCDay() === 6) date = new Date(date.getTime() + DAY);
  return date;
}

class LucidDelivery extends HTMLElement {
  #off = null;
  #timer = 0;

  connectedCallback() {
    this.render();
    this.#off = on('product:variant', ({ detail }) => {
      if (detail.scope && !detail.scope.contains(this)) return;
      this.toggleAttribute('data-unavailable', !detail.available);
      this.render();
    });
    this.#timer = window.setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    this.#off?.();
    window.clearInterval(this.#timer);
  }

  render() {
    if (this.hasAttribute('data-unavailable')) {
      this.hidden = true;
      return;
    }
    const now = zonedParts(new Date(), TZ);
    const [cutHour, cutMinute] = parseTime(this.dataset.cutoff) || [14, 0];
    const processing = Math.max(0, Number(this.dataset.processing) || 0);
    const processingMax = Math.max(processing, Number(this.dataset.processingMax) || processing);
    const min = Math.max(0, Number(this.dataset.min) || 0);
    const max = Math.max(min, Number(this.dataset.max) || min);

    const today = new Date(Date.UTC(now.year, now.month - 1, now.day));
    const isBusinessDay = now.weekday !== 0 && now.weekday !== 6;
    const minutesNow = now.hour * 60 + now.minute;
    const cutoffMinutes = cutHour * 60 + cutMinute;
    const beforeCutoff = isBusinessDay && minutesNow < cutoffMinutes;

    const start = beforeCutoff ? today : nextBusinessDay(new Date(today.getTime() + DAY));
    const earliest = addBusinessDays(addBusinessDays(start, processing), min);
    const latest = addBusinessDays(addBusinessDays(start, processingMax), max);

    const locale = this.dataset.locale || document.documentElement.lang || 'de-DE';
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' });
    } catch {
      formatter = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' });
    }
    const format = (date) => formatter.format(date).replace(/,/g, '');

    const text = this.querySelector('[data-lucid-delivery-text]');
    if (text) {
      text.textContent =
        earliest.getTime() === latest.getTime()
          ? (this.dataset.templateSingle || '').replace('[date]', format(earliest))
          : (this.dataset.templateRange || '').replace('[from]', format(earliest)).replace('[to]', format(latest));
    }

    const cutoff = this.querySelector('[data-lucid-delivery-cutoff]');
    if (cutoff) {
      const left = cutoffMinutes - minutesNow;
      const showCutoff = this.dataset.showCutoff === 'true' && beforeCutoff && left > 0 && left <= 360;
      if (showCutoff) {
        const hours = Math.floor(left / 60);
        const minutes = left % 60;
        const time = hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')} h` : `${minutes} min`;
        cutoff.textContent = (this.dataset.templateCutoff || '').replace('[time]', time).replace('[date]', format(earliest));
      } else {
        cutoff.textContent = '';
      }
    }
    this.hidden = false;
  }
}

if (!customElements.get('lucid-delivery')) customElements.define('lucid-delivery', LucidDelivery);

/* Cart */

function openCart() {
  emit('cart:open');
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return;
  }
  if (!document.querySelector('cart-drawer-component')) {
    window.location.href = window.Theme?.routes?.cart_url || '/cart';
  }
}

/* Bundle "Complete the Fit". */

class LucidBundle extends HTMLElement {
  #off = null;

  connectedCallback() {
    this.addEventListener('change', this.#update);
    this.querySelector('[data-lucid-bundle-add]')?.addEventListener('click', this.#add);
    this.#off = on('product:variant', ({ detail }) => {
      if (!detail.variantId || (detail.scope && !detail.scope.contains(this))) return;
      const select = this.querySelector('[data-current] select[data-lucid-bundle-select]');
      if (select && Array.from(select.options).some((option) => option.value === detail.variantId)) {
        select.value = detail.variantId;
        this.#update();
      }
    });
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#update);
    this.#off?.();
  }

  get #rows() {
    return Array.from(this.querySelectorAll('[data-lucid-bundle-row]')).map((row) => {
      const control = row.querySelector('[data-lucid-bundle-select]');
      const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
      return {
        row,
        title: row.dataset.productTitle || '',
        id: control?.value || '',
        price: Number(option?.dataset.price || 0),
        compare: Number(option?.dataset.compare || option?.dataset.price || 0),
        available: option?.dataset.available === 'true',
      };
    });
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount_with_comma_separator}} €', this.dataset.currency || 'EUR');
  }

  #update = () => {
    const rows = this.#rows;
    const saving = Math.min(Math.max(Number(this.dataset.savingPercent || 0), 0), 90);
    const full = rows.reduce((sum, row) => sum + row.price, 0);
    const total = Math.round(full * (1 - saving / 100));
    const compare = rows.reduce((sum, row) => sum + Math.max(row.compare, row.price), 0);
    const totalElement = this.querySelector('[data-lucid-bundle-total]');
    if (totalElement) {
      totalElement.classList.toggle('lucid-price--sale', compare > total);
      totalElement.innerHTML = '';
      const current = document.createElement('span');
      current.className = 'lucid-price__current';
      current.textContent = this.#money(total);
      totalElement.append(current);
      if (compare > total) {
        const struck = document.createElement('s');
        struck.className = 'lucid-price__compare';
        struck.textContent = this.#money(compare);
        totalElement.append(' ', struck);
      }
    }

    rows.forEach((row) => {
      const price = row.row.querySelector('[data-lucid-bundle-price] .lucid-price__current');
      if (price) price.textContent = this.#money(row.price);
      row.row.classList.toggle('is-unavailable', !row.available);
    });

    const pending = rows.find((row) => !row.id);
    const soldOut = rows.find((row) => row.id && !row.available);
    const button = this.querySelector('[data-lucid-bundle-add]');
    const reason = this.querySelector('[data-lucid-bundle-reason]');
    if (button instanceof HTMLButtonElement) button.disabled = Boolean(pending || soldOut);
    if (reason) {
      const template = soldOut ? this.dataset.unavailableTemplate : pending ? this.dataset.chooseTemplate : '';
      reason.textContent = (template || '').replace('[product]', (soldOut || pending)?.title || '');
    }
  };

  #add = async (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const rows = this.#rows;
    if (rows.some((row) => !row.available || !row.id)) return;

    button.setAttribute('aria-busy', 'true');
    const reason = this.querySelector('[data-lucid-bundle-reason]');
    try {
      await addToCart(
        rows.map((row) => ({ id: row.id, quantity: 1 })),
        { source: this, origin: 'lucid-bundle' }
      );
      if (reason) reason.textContent = '';
      openCart();
    } catch (error) {
      if (reason) reason.textContent = (error?.handled && error.message) || this.dataset.errorText || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('lucid-bundle')) customElements.define('lucid-bundle', LucidBundle);

/* Sticky buy bar: shown once the main buy button has scrolled out above, hidden at the footer. Drives the main form. */

class LucidBuybar extends HTMLElement {
  #controller = null;
  #frame = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.querySelector('[data-lucid-buybar-sheet]')?.addEventListener('click', this.#onSheetClick, { signal });
    this.querySelector('[data-lucid-buybar-sheet]')?.addEventListener('close', () => this.#unlock(), { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#section.addEventListener?.(StandardEvents.productSelect, (event) => event.promise?.then(this.#schedule).catch(() => {}), { signal });
    const offScroll = onPageScroll(this.#schedule);
    signal.addEventListener('abort', () => offScroll?.());
    this.#refresh();
    window.setTimeout(this.#refresh, 600);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    this.#unlock();
  }

  get #section() {
    return document.getElementById(`shopify-section-${this.dataset.sectionId}`) || this.closest('.shopify-section') || document;
  }

  get #form() {
    return this.#section.querySelector('.product-details product-form-component form[data-type="add-to-cart-form"]');
  }

  get #mainButton() {
    return this.#section.querySelector('.product-details product-form-component [ref="addToCartButton"]');
  }

  get #picker() {
    return this.#section.querySelector('variant-picker[data-template-product-match="true"]');
  }

  get #core() {
    return this.#section.querySelector('lucid-pdp-core');
  }

  /** The main buy action as shown: the buy button, else the notify button of a sold-out product, else the form. */
  #target() {
    const candidates = [
      this.#mainButton,
      this.#section.querySelector('.product-details [data-lucid-notify]'),
      this.#section.querySelector('.product-details product-form-component'),
      this.#form,
    ];
    return candidates.find((element) => element instanceof HTMLElement && element.getClientRects().length > 0 && element.offsetHeight > 0) || null;
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#refresh);
  };

  #refresh = () => {
    const target = this.#target();
    const rect = target?.getBoundingClientRect();
    // Visible whenever the main buy button is off screen, above or still below (on phones it sits about two screens
    // down), once the visitor has started to scroll so the gallery controls stay free on arrival.
    const above = target ? rect.bottom < 0 || (rect.top > window.innerHeight && pageScrollTop() > 120) : pageScrollTop() > 600;
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    const atFooter = footer instanceof HTMLElement && footer.getBoundingClientRect().top < window.innerHeight - 24;
    const show = above && !atFooter;
    if (show) this.hidden = false;
    this.classList.toggle('is-visible', show);
    this.toggleAttribute('inert', !show);
    if (!show) {
      const sheet = this.querySelector('[data-lucid-buybar-sheet]');
      if (sheet instanceof HTMLDialogElement && sheet.open) sheet.close();
    }
  };

  #unpicked() {
    return this.#section instanceof HTMLElement && this.#section.dataset.lucidSize === 'none';
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-lucid-buybar-size]')) {
      this.#openSheet();
      return;
    }
    if (target.closest('[data-lucid-buybar-notify]')) {
      this.#core?.notify?.();
      return;
    }
    const button = target.closest('[data-lucid-buybar-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (this.#unpicked()) {
      this.#openSheet();
      return;
    }
    const form = this.#form;
    const submit = this.#mainButton;
    if (!(form instanceof HTMLFormElement) || (submit instanceof HTMLButtonElement && submit.disabled)) return;
    button.dataset.lucidState = 'loading';
    if (submit instanceof HTMLButtonElement) form.requestSubmit(submit);
    else form.requestSubmit();
  };

  #openSheet() {
    const sheet = this.querySelector('[data-lucid-buybar-sheet]');
    const grid = this.querySelector('[data-lucid-sheet-sizes]');
    const picker = this.#picker;
    if (!(sheet instanceof HTMLDialogElement) || !grid || !picker) return;
    grid.replaceChildren();
    picker.querySelectorAll('.variant-option--buttons').forEach((fieldset) => {
      const group = document.createElement('div');
      group.className = 'lucid-buybar__sheet-group';
      fieldset.querySelectorAll('label').forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        if (!(input instanceof HTMLInputElement)) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lucid-size lucid-focus';
        button.dataset.inputId = input.dataset.inputId || '';
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', String(input.checked));
        button.setAttribute('aria-label', input.getAttribute('aria-label') || input.value);
        if (label.classList.contains('is-preorder')) button.classList.add('is-preorder');
        else if (input.dataset.optionAvailable === 'false') button.classList.add('is-sold-out');
        const face = label.querySelector('.lucid-size__face');
        if (face) button.append(face.cloneNode(true));
        else button.textContent = input.value;
        group.append(button);
      });
      grid.append(group);
    });
    document.documentElement.setAttribute('scroll-lock', '');
    sheet.showModal();
    const first = grid.querySelector('[aria-checked="true"]') || grid.querySelector('.lucid-size');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  #onSheetClick = (event) => {
    const sheet = event.currentTarget;
    const target = event.target instanceof Element ? event.target : null;
    if (!(sheet instanceof HTMLDialogElement) || !target) return;
    if (target === sheet || target.closest('[data-lucid-sheet-close]')) {
      sheet.close();
      return;
    }
    const choice = target.closest('[data-input-id]');
    if (!(choice instanceof HTMLElement)) return;
    const input = this.#picker?.querySelector(`input[data-input-id="${CSS.escape(choice.dataset.inputId || '')}"]`);
    if (input instanceof HTMLInputElement) {
      if (input.checked) this.#core?.pick?.(input);
      else input.click();
    }
    window.setTimeout(() => sheet.close(), prefersReducedMotion() ? 0 : 240);
  };

  #unlock() {
    if (!document.querySelector('dialog.lucid-lightbox[open], dialog.lucid-buybar__sheet[open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  #onCartUpdate = (event) => {
    const button = this.querySelector('[data-lucid-buybar-add]');
    if (!(button instanceof HTMLElement) || button.dataset.lucidState !== 'loading') return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) {
          delete button.dataset.lucidState;
          return;
        }
        button.dataset.lucidState = 'done';
        window.setTimeout(() => delete button.dataset.lucidState, 1800);
      })
      .catch(() => delete button.dataset.lucidState);
  };
}

if (!customElements.get('lucid-buybar')) customElements.define('lucid-buybar', LucidBuybar);

/* Wash rail: more siblings than two rows hold scroll in one row, with fades and arrows at the open ends. */

class LucidWashesRail extends HTMLElement {
  #controller = null;
  #frame = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const list = this.list;
    if (!list) return;
    list.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.querySelector('[data-lucid-rail-prev]')?.addEventListener('click', () => this.#page(-1), { signal });
    this.querySelector('[data-lucid-rail-next]')?.addEventListener('click', () => this.#page(1), { signal });
    const current = list.querySelector('[aria-current="page"]')?.closest('li');
    if (current instanceof HTMLElement && current.offsetLeft + current.offsetWidth > list.clientWidth) {
      list.scrollLeft = current.offsetLeft - (list.clientWidth - current.offsetWidth) / 2;
    }
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get list() {
    const list = this.querySelector('[data-lucid-rail]');
    return list instanceof HTMLElement ? list : null;
  }

  #page(direction) {
    const list = this.list;
    if (!list) return;
    list.scrollBy({ left: direction * list.clientWidth * 0.75, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const list = this.list;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    const start = list.scrollLeft > 4;
    const end = list.scrollLeft < max - 4;
    this.toggleAttribute('data-more-start', start);
    this.toggleAttribute('data-more-end', end);
    const prev = this.querySelector('[data-lucid-rail-prev]');
    const next = this.querySelector('[data-lucid-rail-next]');
    if (prev instanceof HTMLElement) prev.hidden = !start;
    if (next instanceof HTMLElement) next.hidden = !end;
  }
}

if (!customElements.get('lucid-washes-rail')) customElements.define('lucid-washes-rail', LucidWashesRail);

/* Recently viewed: records the product (Horizon's store) and renders the others through the search section. */

class LucidRecent extends HTMLElement {
  connectedCallback() {
    const current = this.dataset.productId;
    let viewed = [];
    try {
      viewed = RecentlyViewed.getProducts().filter((id) => String(id) !== String(current));
      if (current) RecentlyViewed.addProduct(String(current));
    } catch {
      return;
    }
    if (!viewed.length || this.dataset.loaded) return;
    this.dataset.loaded = 'true';
    this.#load(viewed.slice(0, Number(this.dataset.limit) || 4));
  }

  async #load(ids) {
    const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', this.dataset.sectionId || '');
    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('lucid-recent');
      if (!next || !next.children.length) return;
      const list = next.querySelector('[data-lucid-recent-list]');
      if (list) {
        const order = new Map(ids.map((id, index) => [String(id), index]));
        Array.from(list.children)
          .sort((a, b) => (order.get(a.getAttribute('data-product-id')) ?? 99) - (order.get(b.getAttribute('data-product-id')) ?? 99))
          .forEach((item) => list.append(item));
      }
      this.replaceChildren(...Array.from(next.childNodes).map((node) => document.importNode(node, true)));
    } catch (error) {
      console.warn(error);
    }
  }
}

if (!customElements.get('lucid-recent')) customElements.define('lucid-recent', LucidRecent);

/* Amounts set in the shop currency (free shipping thresholds), shown converted with the storefront rate. */

class LucidMoney extends HTMLElement {
  connectedCallback() {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    const cents = Math.round(Number(this.dataset.cents || 0) * rate);
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'EUR';
    if (!cents) return;
    try {
      this.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch {
      this.textContent = String(Math.round(cents / 100));
    }
  }
}

if (!customElements.get('lucid-money')) customElements.define('lucid-money', LucidMoney);

// Fit box: measurements panel opens on demand (collapsed by default so the buy area stays short).
document.addEventListener('click', (event) => {
  const toggle = event.target.closest?.('[data-lucid-fit-toggle]');
  if (!toggle) return;
  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;
  const open = toggle.getAttribute('aria-expanded') !== 'true';
  toggle.setAttribute('aria-expanded', String(open));
  panel.hidden = !open;
});
